/**
 * Mobile device detection utility for ENG-2465
 *
 * Used to skip PDF preloading on mobile devices where:
 * - Memory is more constrained
 * - Users typically view fewer documents per session
 * - Network conditions may be less predictable
 *
 * Exception: Large tablets (>= 1024px) are treated like desktops
 * since they have sufficient memory and users may view multiple documents.
 */

// Minimum screen width for "large tablet" classification
const LARGE_TABLET_MIN_WIDTH = 1024;

// Mobile user agent patterns
const MOBILE_PATTERNS = [
  /android/i,
  /webos/i,
  /iphone/i,
  /ipad/i,
  /ipod/i,
  /blackberry/i,
  /windows phone/i,
  /opera mini/i,
  /iemobile/i,
  /mobile/i,
];

// Tablet patterns (iPad, Android tablets without "mobile" in UA)
const TABLET_PATTERNS = [
  /ipad/i,
  /android(?!.*mobile)/i, // Android without "mobile" = tablet
  /tablet/i,
];

/**
 * Read browser environment signals. Accepts an optional override for testing
 * (happy-dom's Navigator properties are not mockable via Object.defineProperty).
 */
const getEnv = (override) => {
  if (override) return override;
  return {
    userAgent: window.navigator.userAgent || window.navigator.vendor || window.opera,
    maxTouchPoints: window.navigator.maxTouchPoints || 0,
    innerWidth: window.innerWidth,
    hasOntouchstart: 'ontouchstart' in window,
  };
};

/**
 * Check if the current device is a mobile device
 * Uses user agent detection and screen size as fallback
 * @param {object} [env] - Optional environment override for testing
 * @returns {boolean}
 */
export const isMobileDevice = (env) => {
  const { userAgent, maxTouchPoints, innerWidth, hasOntouchstart } = getEnv(env);

  if (MOBILE_PATTERNS.some((pattern) => pattern.test(userAgent))) {
    return true;
  }

  // Fallback: consider mobile if small screen AND touch device
  // This avoids false positives from desktop browsers with small windows
  const isSmallScreen = innerWidth < 768;
  const isTouchDevice = hasOntouchstart || maxTouchPoints > 0;
  return isSmallScreen && isTouchDevice;
};

/**
 * Check if the device is a tablet with a large screen (>= 1024px)
 * @param {object} [env] - Optional environment override for testing
 * @returns {boolean}
 */
const isLargeTablet = (env) => {
  const { userAgent, innerWidth } = getEnv(env);

  const isTablet = TABLET_PATTERNS.some((pattern) => pattern.test(userAgent));
  if (!isTablet) return false;

  return innerWidth >= LARGE_TABLET_MIN_WIDTH;
};

/**
 * Check if preloading should be enabled based on device type
 * Enabled for: desktops, large tablets (>= 1024px)
 * Disabled for: phones, small tablets
 * @param {object} [env] - Optional environment override for testing
 * @returns {boolean}
 */
export const shouldEnablePreloading = (env) => {
  if (isLargeTablet(env)) return true;
  return !isMobileDevice(env);
};

export default isMobileDevice;
